<div class="bg-white rounded-3xl h-full w-full p-6 flex flex-col gap-6">
  <div class="h-full w-full overflow-auto p-2">
    <p class="text-xl oswald-font font-medium text-gray-500 pb-4">360 Bulk Upload</p>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- LEFT: form -->
      <form (ngSubmit)="submit()" class="flex flex-col gap-5">
        <div
          *ngIf="!selectedBuilding()"
          class="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-xs text-amber-800">
          Select a building from the top navbar to continue.
        </div>

        <!-- Persistent error banner — clears on next submit. The transient
             toast in the corner is easy to miss; this stays put. -->
        <div
          *ngIf="errorBanner() as e"
          [ngClass]="
            e.kind === 'conflict'
              ? 'bg-amber-50 border-amber-300 text-amber-900'
              : 'bg-red-50 border-red-300 text-red-900'
          "
          class="border rounded-md px-3 py-2.5 text-xs flex items-start justify-between gap-2">
          <div>
            <span class="font-bold">
              <ng-container *ngIf="e.kind === 'conflict'">Upload blocked:</ng-container>
              <ng-container *ngIf="e.kind === 'generic'">Upload failed:</ng-container>
            </span>
            {{ e.message }}
          </div>
          <button
            type="button"
            (click)="errorBanner.set(null)"
            class="text-xs opacity-60 hover:opacity-100">
            ✕
          </button>
        </div>

        <div>
          <label for="bulkLevelSelect" class="block mb-2 text-sm font-bold text-gray-900">Level</label>
          <select
            id="bulkLevelSelect"
            [disabled]="!selectedBuilding() || loadingLevels() || busy()"
            [ngModel]="selectedLevelId()"
            (ngModelChange)="selectedLevelId.set($event)"
            name="levelSelect"
            class="bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 disabled:opacity-60">
            <option [ngValue]="null">
              <ng-container *ngIf="!selectedBuilding()">-- Select a building first --</ng-container>
              <ng-container *ngIf="selectedBuilding() && loadingLevels()">-- Loading… --</ng-container>
              <ng-container *ngIf="selectedBuilding() && !loadingLevels() && levels().length === 0">
                -- No levels available --
              </ng-container>
              <ng-container *ngIf="selectedBuilding() && !loadingLevels() && levels().length > 0">
                -- Select a level --
              </ng-container>
            </option>
            <option *ngFor="let lvl of levels()" [ngValue]="lvl.id">
              {{ lvl.name }} ({{ lvl.id }})
            </option>
          </select>
        </div>
  
        <div>
          <label for="bulkZip" class="block mb-2 text-sm font-bold text-gray-900">1. Zipped Raw 360 Images</label>
          <input
            id="bulkZip"
            type="file"
            accept=".zip"
            (change)="onZipChange($event)"
            [disabled]="busy()"
            class="block w-full text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-50 file:bg-zinc-800 file:text-white file:border-0 file:px-4 file:py-2.5 file:mr-3 cursor-pointer" />
        </div>
  
        <div>
          <label for="bulkGeo" class="block mb-2 text-sm font-bold text-gray-900">2. Fulcrum GeoJSON File</label>
          <input
            id="bulkGeo"
            type="file"
            accept=".json,.geojson,application/json"
            (change)="onGeoJsonChange($event)"
            [disabled]="busy()"
            class="block w-full text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-50 file:bg-zinc-800 file:text-white file:border-0 file:px-4 file:py-2.5 file:mr-3 cursor-pointer" />
        </div>
  
        <div>
          <label for="bulkLogo" class="block mb-2 text-sm font-bold text-gray-900">
            3. Logo Emblem <span class="font-normal text-gray-500">(Optional)</span>
          </label>
          <input
            id="bulkLogo"
            type="file"
            accept=".png,image/png"
            (change)="onLogoChange($event)"
            [disabled]="busy()"
            class="block w-full text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-50 file:bg-zinc-800 file:text-white file:border-0 file:px-4 file:py-2.5 file:mr-3 cursor-pointer" />
        </div>
  
        <label class="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            [checked]="replaceExisting()"
            (change)="replaceExisting.set($any($event.target).checked)"
            [disabled]="busy()"
            class="w-4 h-4 mt-0.5 accent-nside-orange-400" />
          <span class="text-xs text-gray-700">
            <span class="font-bold text-red-700">Replace existing 360 data for this level</span><br />
            Permanently deletes every streetview + feature + image blob currently linked to this level
            before the new batch is uploaded. Streetviews on other levels are untouched. Sent only on
            the first batch so subsequent batches don't purge each other's commits.
          </span>
        </label>

        <div class="flex justify-end items-center gap-3">
          <button
            type="submit"
            [disabled]="busy() || !selectedBuilding || !selectedLevelId() || !zipFile() || !geojsonFile()"
            class="px-6 py-2 rounded-md bg-blue-500 text-white oswald-font disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Upload
          </button>
          <button
            type="button"
            (click)="cancel()"
            [disabled]="!busy()"
            class="px-6 py-2 rounded-md border border-gray-400 oswald-font disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </form>
  
      <!-- RIGHT: import details -->
      <div class="flex flex-col gap-4">
        <p class="oswald-font text-lg font-medium border-b border-gray-300 pb-1">360 Import Details</p>
        <ul class="text-sm space-y-1">
          <li>
            <span class="font-bold">Building ID:</span>
            <span class="ml-1 text-gray-700">{{ selectedBuilding()?.id ?? '—' }}</span>
          </li>
          <li>
            <span class="font-bold">Level ID:</span>
            <span class="ml-1 text-gray-700">{{ selectedLevelId() ?? '—' }}</span>
          </li>
          <li>
            <span class="font-bold">Target:</span>
            <span class="ml-1 text-gray-500 break-all">{{ targetUrl() }}</span>
          </li>
        </ul>
      </div>
    </div>
  
    <!-- Results -->
    <div *ngIf="showResults()" class="w-full flex flex-col gap-3">
      <p class="oswald-font text-sm font-medium">Results</p>
  
      <div
        *ngIf="matchedCount() > 0"
        class="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
        <span class="font-bold">Matched:</span> {{ matchedCount() }} photo(s) linked to a GeoJSON point.
      </div>
  
      <div
        *ngIf="skippedRows().length > 0"
        class="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
        <span class="font-bold">Photos skipped</span> ({{ skippedRows().length }}): nothing was written
        to the database or cloud storage for these.
        <ng-container *ngFor="let group of skippedByReason()">
          <div class="font-semibold mt-2">{{ group.label }} ({{ group.rows.length }})</div>
          <div *ngFor="let r of group.rows.slice(0, 30)">
            • {{ r.fileName }}
            <ng-container *ngIf="r.pointId !== null && r.pointId !== undefined">
              (point_id={{ r.pointId }})
            </ng-container>
            <ng-container *ngIf="r.pointId === null || r.pointId === undefined">
              (no parseable point_id)
            </ng-container>
            <ng-container *ngIf="r.existingStreetviewId">
              — existing streetview ID: {{ r.existingStreetviewId }}
            </ng-container>
          </div>
          <div *ngIf="group.rows.length > 30" class="italic text-sky-700">
            …and {{ group.rows.length - 30 }} more in this group.
          </div>
        </ng-container>
      </div>
  
      <div
        *ngIf="unmatchedJpgs().length > 0"
        class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <span class="font-bold">Orphan Streetviews detected</span> ({{ unmatchedJpgs().length }}):
        these rows landed in the DB without a Feature row. Re-run the upload with <em>Replace existing</em>
        to clean them up.
        <div *ngFor="let r of unmatchedJpgs().slice(0, 30)" class="text-amber-900">
          • {{ r.fileName }}
          <ng-container *ngIf="r.pointId !== null && r.pointId !== undefined">
            (point_id={{ r.pointId }})
          </ng-container>
          <ng-container *ngIf="r.pointId === null || r.pointId === undefined">
            (no parseable point_id)
          </ng-container>
        </div>
        <div *ngIf="unmatchedJpgs().length > 30" class="italic text-amber-700">
          …and {{ unmatchedJpgs().length - 30 }} more.
        </div>
      </div>
  
      <div
        *ngIf="unmatchedPointIds().length > 0"
        class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <span class="font-bold">GeoJSON points without a photo</span> ({{ unmatchedPointIds().length }}):
        the Fulcrum survey had these point_ids but no matching JPG arrived.
        <div class="mt-1 text-amber-900 break-words">
          {{ unmatchedPointIds().slice(0, 60).join(', ') }}
          <ng-container *ngIf="unmatchedPointIds().length > 60">
            , …and {{ unmatchedPointIds().length - 60 }} more
          </ng-container>
        </div>
      </div>
  
      <div
        *ngIf="unindexableCount() > 0"
        class="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
        <span class="font-bold">Skipped GeoJSON features</span> ({{ unindexableCount() }}): missing
        <code>properties.point_id</code> or <code>geometry</code>. Fix Fulcrum export and re-upload to
        link these.
      </div>
  
      <div *ngIf="resultRows().length > 0" class="w-full overflow-x-auto border border-gray-300 rounded-md">
        <table class="w-full text-xs">
          <thead class="bg-gray-100">
            <tr class="text-left">
              <th class="px-3 py-2">Streetview ID</th>
              <th class="px-3 py-2">File</th>
              <th class="px-3 py-2">Point ID</th>
              <th class="px-3 py-2">Matched</th>
              <th class="px-3 py-2">Processing</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let row of resultRows()"
              class="border-t border-gray-200"
              [ngClass]="{ 'bg-amber-50': !row.matched }">
              <td class="px-3 py-1">{{ row.streetviewId }}</td>
              <td class="px-3 py-1">{{ row.fileName }}</td>
              <td class="px-3 py-1">{{ row.pointId ?? '—' }}</td>
              <td class="px-3 py-1">
                <span *ngIf="row.matched" class="text-green-700">✓ matched</span>
                <span *ngIf="!row.matched" class="text-amber-700">— no match</span>
              </td>
              <td
                class="px-3 py-1"
                [ngClass]="{
                  'text-green-700': row.processing.status === 'success',
                  'text-red-700': row.processing.status !== 'success'
                }">
                {{ row.processing.status || 'unknown' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>

<!-- Upload overlay: full-screen so the user can't accidentally interact with
     the form mid-upload. Combines a spinner with a two-phase progress bar
     so they always know which batch is in flight and how far along the
     entire sequence is. -->
<div
  *ngIf="progress() as p"
  class="fixed inset-0 flex items-center justify-center bg-main-background bg-opacity-75 z-30">
  <div class="p-8 rounded-md text-center bg-white/95 shadow-xl border border-gray-200 min-w-[360px] max-w-[440px]">
    <div class="flex flex-col items-center justify-center gap-4">
      <div role="status">
        <svg
          aria-hidden="true"
          class="inline h-10 w-10 animate-spin text-gray-200 fill-[#f25d22]"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor" />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill" />
        </svg>
        <span class="sr-only">Uploading…</span>
      </div>

      <p class="oswald-font text-lg text-gray-800">
        <ng-container [ngSwitch]="p.phase">
          <span *ngSwitchCase="'reading'">Reading ZIP…</span>
          <span *ngSwitchCase="'uploading'">
            Uploading batch {{ p.currentBatch }} of {{ p.totalBatches }}
          </span>
          <span *ngSwitchCase="'processing'">
            Processing batch {{ p.currentBatch }} of {{ p.totalBatches }}
          </span>
          <span *ngSwitchDefault>Working…</span>
        </ng-container>
      </p>

      <div class="w-full">
        <div class="flex justify-between text-xs text-gray-600 mb-1">
          <span>Overall</span>
          <span class="tabular-nums">{{ p.overallPercent.toFixed(1) }}%</span>
        </div>
        <div class="w-full h-3 bg-gray-200 rounded-md overflow-hidden">
          <div
            class="h-full bg-blue-500 hover:bg-blue-600 transition-all duration-150"
            [style.width.%]="p.overallPercent"></div>
        </div>

        <div *ngIf="p.totalBatches > 0" class="flex justify-between text-xs text-gray-600 mt-3 mb-1">
          <span>This batch</span>
          <span class="tabular-nums">{{ p.batchPercent.toFixed(0) }}%</span>
        </div>
        <div *ngIf="p.totalBatches > 0" class="w-full h-2 bg-gray-100 rounded-md overflow-hidden">
          <div
            class="h-full bg-blue-500 hover:bg-blue-600 transition-all duration-150"
            [style.width.%]="p.batchPercent"></div>
        </div>

        <p *ngIf="p.totalJpgs > 0" class="mt-3 text-xs text-gray-500">
          {{ p.totalJpgs }} photo(s) split into {{ p.totalBatches }} batch{{
            p.totalBatches === 1 ? '' : 'es'
          }}.
        </p>
      </div>

      <button
        type="button"
        (click)="cancel()"
        class="mt-2 px-5 py-1.5 rounded-md border border-gray-400 text-sm oswald-font hover:bg-gray-50">
        Cancel
      </button>
    </div>
  </div>
</div>
